<script lang="ts" setup>
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import { i18n } from '@/i18n';
import { useEditIndividualBusinessStore } from '../pages/useEditIndividualBusinessStore';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import {
  formOptionsAggregateBusiness,
  formOptionsBusiness,
  inputTypeKeyMap,
  keyMappingForSelectInput,
  validateRuleKeyMap,
  unitOptions,
  unitOptionsMapping,
} from '../constants';
import { ref, watch } from 'vue';
import { GetBusinessNameUseCase } from '@/coreUseCasesModule/getItem/GetBusinessNameUseCase';
import { GetBusinessNumberUseCase } from '@/coreUseCasesModule/getItem/GetBusinessNumberUseCase';
import { GetWarehouseNameUseCase } from '@/coreUseCasesModule/getItem/GetWarehouseNameUseCase';
import { GetWarehouseNumberUseCase } from '@/coreUseCasesModule/getItem/GetWarehouseNumberUseCase';
import { GetSupplierNameUseCase } from '@/coreUseCasesModule/getItem/GetSupplierNameUseCase';
import { GetSupplierNumberUseCase } from '@/coreUseCasesModule/getItem/GetSupplierNumberUseCase';
import { GetCustomerNameUseCase } from '@/coreUseCasesModule/getItem/GetCustomerNameUseCase';
import { GetCustomerNumberUseCase } from '@/coreUseCasesModule/getItem/GetCustomerNumberUseCase';
import { GetSalesDestinationNameUseCase } from '@/coreUseCasesModule/getItem/GetSalesDestinationNameUseCase';
import { GetSalesDestinationNumberUseCase } from '@/coreUseCasesModule/getItem/GetSalesDestinationNumberUseCase';
import { AggregateBusinessRepository } from '@/coreInfrastructureModule/Repositories/AggregateBusinessRepository';
import { BaseBusinessManageRepository } from '@/coreInfrastructureModule/Repositories/BaseBusinessManageRepository';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import AddOptionSection from '@/corePagesModule/updateBusinessModal/components/AddOptionSection.vue';
import { diContainer } from '@/coreContainer/container';
import { convertLeadTimeByUnit } from '@/coreDomainsModule/leadtime';

const t = i18n.t;
const store = useEditIndividualBusinessStore();
const { state } = storeToRefs(store);

const aggregateBusinessRepository = new AggregateBusinessRepository();
const baseBusinessManageRepository = new BaseBusinessManageRepository();

const fieldItems = computed((): FieldItem[] => {
  const targetArray = state.value.modalState.editState.type === 'aggregateBusiness' ? formOptionsAggregateBusiness : formOptionsBusiness;
  const items = targetArray.map((name: string) => {
    return {
      name,
      rules: validateRuleKeyMap[name],
    };
  });
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(fieldItems.value);
const currentOpenSelectInputName = ref('');

/**
 * 編集モーダルの保存ボタン押下時の処理
 */
const editingModalOnNext = async () => {
  if (allValidate()) {
    if (isEditAggregateBusiness.value) {
      state.value.modalState.editState.item = BusinessFactory.create({
        ...state.value.modalState.editState.item,
        warehouseNumber: fieldState['warehouseNumber'].value.value,
        warehouse: fieldState['warehouseName'].value.value,
        supplierNumber: fieldState['supplierNumber'].value.value,
        supplierName: fieldState['supplierName'].value.value,
        businessNumber: fieldState['businessNumber'].value.value,
        businessName: fieldState['businessName'].value.value,
        package: fieldState['package'].value.value,
        unit: fieldState['unit'].value.value,
        manufacturer: fieldState['manufacturer'].value.value,
        leadTime: Number(fieldState['leadTime'].value.value) || 0,
        leadTimeUnit: store.convertDateUnit(fieldState['leadTimeUnit'].value.value),
        MOQ: Number(fieldState['MOQ'].value.value) || 0,
        stdInventoryIndices: {
          標準在庫指数: {
            max: Number(fieldState['stdInventoryIndicesMax'].value.value) || 0,
            min: Number(fieldState['stdInventoryIndicesMin'].value.value) || 0,
            option: {
              unit: store.convertDateUnit(fieldState['stdInventoryIndicesUnit'].value.value),
            },
          },
        },
      });
      state.value.modalState.isOpening.childBusinessList = true;
      state.value.modalState.isOpening.edit = false;
      await store.fetchChildBusiness();
    } else {
      state.value.modalState.editState.item = BusinessFactory.create({
        ...state.value.modalState.editState.item,
        customerNumber: fieldState['customerNumber'].value.value,
        customerName: fieldState['customerName'].value.value,
        salesDestinationNumber: fieldState['salesDestinationNumber'].value.value,
        salesDestinationName: fieldState['salesDestinationName'].value.value,
        endUser: fieldState['endUser'].value.value,
      });
      state.value.modalState.isOpening.loading = true;
      state.value.modalState.isOpening.edit = false;
      await store.putEditedBaseBusiness();
    }
  }
};

/**
 * フォームのリストを見た目上で分かれている部分で分割して取得する
 */
const getDividedFormOptions = computed(() => {
  return [formOptionsAggregateBusiness.slice(0, 9), formOptionsAggregateBusiness.slice(9)];
});

/**
 * 編集しているデータが集計かどうか
 */
const isEditAggregateBusiness = computed(() => {
  return state.value.modalState.editState.type === 'aggregateBusiness';
});

/**
 * モーダルを閉じる処理
 */
const closeEditModal = () => {
  state.value.modalState.isOpening.edit = false;
  if (!store.isModalShown()) {
    store.resetEditModalState();
  }
};

/**
 * モーダルを開いた際の処理
 */
watch(
  () => state.value.modalState.isOpening.edit,
  (newVal) => {
    if (newVal) {
      if (state.value.modalState.editState.type === 'aggregateBusiness') {
        const targetLeadTimeUnit = state.value.modalState.editState.item['leadTimeUnit'];
        const targetStdInventoryIndices = state.value.modalState.editState.item.stdInventoryIndices;
        resetForm(fieldItems.value);
        for (let i = 0; i < fieldItems.value.length; i++) {
          const keyName = fieldItems.value[i].name;
          switch (keyName) {
            case 'warehouseName':
              fieldState[keyName].setValue(state.value.modalState.editState.item['warehouse'], false);
              break;

            case 'leadTime':
              fieldState[keyName].setValue(
                convertLeadTimeByUnit(state.value.modalState.editState.item['leadTime'] ?? 0, targetLeadTimeUnit ?? '').toString()
              );
              break;

            case 'leadTimeUnit':
              fieldState[keyName].setValue(targetLeadTimeUnit ? t(unitOptionsMapping[targetLeadTimeUnit]) : '', false);
              break;

            case 'stdInventoryIndicesMin':
              fieldState[keyName].setValue(
                targetStdInventoryIndices?.標準在庫指数?.min ? targetStdInventoryIndices['標準在庫指数'].min.toString() : '',
                false
              );
              break;

            case 'stdInventoryIndicesMax':
              fieldState[keyName].setValue(
                targetStdInventoryIndices?.標準在庫指数?.max ? targetStdInventoryIndices['標準在庫指数'].max.toString() : '',
                false
              );
              break;

            case 'stdInventoryIndicesUnit':
              fieldState[keyName].setValue(
                targetStdInventoryIndices?.標準在庫指数?.option.unit
                  ? t(unitOptionsMapping[targetStdInventoryIndices['標準在庫指数'].option.unit])
                  : '',
                false
              );
              break;

            default:
              fieldState[keyName].setValue(state.value.modalState.editState.item[keyName], false);
          }
        }
      } else {
        resetForm(fieldItems.value);
        for (let i = 0; i < fieldItems.value.length; i++) {
          const keyName = fieldItems.value[i].name;
          fieldState[keyName].setValue(state.value.modalState.editState.item[keyName] ?? '', false);
        }
      }
    }
  }
);

/**
 * 新規選択肢追加ボタンを押した際の処理
 * @param type - 選択肢の種類
 * @param name - 名前
 * @param code - コード(Package, Manufacturer, EndUserはなし)
 */
const addNewOption = (type: string, name: string, code?: string) => {
  switch (type) {
    case 'package':
    case 'manufacturer':
    case 'endUser':
      fieldState[type].setValue(name);
      break;
    default:
      if (!code) {
        console.error('エラー:取引編集>新規選択肢追加:Codeなし');
        state.value.modalState.isOpening.commonError = true;
        return;
      }
      fieldState[type + 'Name'].setValue(name);
      fieldState[type + 'Number'].setValue(code);
  }
  state.value.modalState.editState.isEdited = true;
  return;
};

/**
 * フォームの値を変更した際の処理
 * @param option - フォームの種類
 * @param value - 値
 */
const updateValue = (option: string, value: string) => {
  state.value.modalState.editState.isEdited = true;
  fieldState[option].setValue(value);
  const keyMap = {
    businessNumber: setBusinessName,
    businessName: setBusinessNumber,
    warehouseNumber: setWarehouseName,
    warehouseName: setWarehouseNumber,
    supplierNumber: setSupplierName,
    supplierName: setSupplierNumber,
    customerNumber: setCustomerName,
    customerName: setCustomerNumber,
    salesDestinationNumber: setSalesDestinationName,
    salesDestinationName: setSalesDestinationNumber,
  };
  if (keyMap[option]) keyMap[option]();
};

/**
 * 汎用的な値設定関数
 * @param useCase - 使用するユースケース
 * @param sourceKey - 取得元のキー
 * @param targetKey - 設定先のキー
 */
const setFieldValue = async (useCase, sourceKey, targetKey) => {
  if (fieldState[sourceKey].value.value === '') return; // 空文字（選択を外す）なら何もしない

  const result: INetworkResult<string | undefined, IError> = await useCase.execute(fieldState[sourceKey].value.value);

  // マッチする値が存在しない場合は何もしない
  if (result.status.type === 'success' && result.status.data !== undefined) {
    fieldState[targetKey].setValue(result.status.data);
  }
};

/** 商品名を設定する */
const setBusinessName = () => setFieldValue(diContainer.getBusinessNameUseCase, 'businessNumber', 'businessName');
/** 商品番号を設定する */
const setBusinessNumber = () => setFieldValue(diContainer.getBusinessNumberUseCase, 'businessName', 'businessNumber');
/** 置き場名を設定する */
const setWarehouseName = () => setFieldValue(diContainer.getWarehouseNameUseCase, 'warehouseNumber', 'warehouseName');
/** 置き場番号を設定する */
const setWarehouseNumber = () => setFieldValue(diContainer.getWarehouseNumberUseCase, 'warehouseName', 'warehouseNumber');
/** 仕入先名を設定する */
const setSupplierName = () => setFieldValue(diContainer.getSupplierNameUseCase, 'supplierNumber', 'supplierName');
/** 仕入先番号を設定する */
const setSupplierNumber = () => setFieldValue(diContainer.getSupplierNumberUseCase, 'supplierName', 'supplierNumber');
/** 売り上げ先名を設定する */
const setCustomerName = () => setFieldValue(diContainer.getCustomerNameUseCase, 'customerNumber', 'customerName');
/** 売り上げ先番号を設定する */
const setCustomerNumber = () => setFieldValue(diContainer.getCustomerNumberUseCase, 'customerName', 'customerNumber');
/** 送り先名を設定する */
const setSalesDestinationName = () =>
  setFieldValue(diContainer.getSalesDestinationNameUseCase, 'salesDestinationNumber', 'salesDestinationName');
/** 送り先番号を設定する */
const setSalesDestinationNumber = () =>
  setFieldValue(diContainer.getSalesDestinationNumberUseCase, 'salesDestinationName', 'salesDestinationNumber');

/**
 * バリデーションにRequiredがある場合は*を返す
 * @param key - 入力項目のキー
 */
const getRequiredMark = (key: string | number) => {
  if (validateRuleKeyMap[key].includes(ValidateRule.Required)) {
    return '*';
  }
  return '';
};
</script>
<template>
  <InfoModal
    :modal-state="state.modalState.isOpening.edit"
    :modal-width="1300"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('保存')"
    :is-disabled-next-button="!state.modalState.editState.isEdited"
    @on-back="closeEditModal"
    @on-next="editingModalOnNext"
  >
    <template #title>{{ t('編集') }}</template>
    <template #description>{{ t('選択済の取引ファイルに対して、項目の内容を修正できます。') }}</template>
    <template #default>
      <template v-if="isEditAggregateBusiness">
        <div class="edit-individual-modal__container edit-individual-modal__container--base-data">
          <template v-for="(option, index) in getDividedFormOptions[0]" :key="option">
            <div
              class="edit-individual-modal__item"
              :class="index < 4 ? 'edit-individual-modal__item--base-data--upper' : 'edit-individual-modal__item--base-data--lower'"
            >
              <template v-if="inputTypeKeyMap[option] === 'select'">
                <SingleSelectInput
                  :index="index"
                  :label="t(option) + getRequiredMark(option)"
                  :model-value="fieldState[option].value.value"
                  :placeholder="t(option)"
                  :filter-type="keyMappingForSelectInput[option]"
                  :is-error="fieldState[option]?.isError.value"
                  :error-message="fieldState[option]?.errorMessage.value"
                  @click:select-input="() => (currentOpenSelectInputName = option)"
                  @update:model-value="(e) => updateValue(option, e)"
                />
              </template>
              <template v-else>
                <TextInput
                  :model-value="fieldState[option].value.value"
                  :label="t(option) + getRequiredMark(option)"
                  :placeholder="t(option)"
                  :is-error="fieldState[option].isError.value"
                  :error-message="fieldState[option].errorMessage.value"
                  @update:model-value="(e) => updateValue(option, e)"
                />
              </template>
            </div>
          </template>
        </div>
        <div class="edit-individual-modal__container edit-individual-modal__container--amount">
          <template v-for="(option, index) in getDividedFormOptions[1]" :key="option">
            <div class="edit-individual-modal__item">
              <template v-if="inputTypeKeyMap[option] === 'select'">
                <SingleSelectInput
                  :index="index + 9"
                  :label="t(option) + getRequiredMark(option)"
                  :model-value="fieldState[option].value.value"
                  :placeholder="t(option)"
                  :filter-type="keyMappingForSelectInput[option]"
                  :is-error="fieldState[option]?.isError.value"
                  :error-message="fieldState[option]?.errorMessage.value"
                  @click:select-input="() => (currentOpenSelectInputName = option)"
                  @update:model-value="(e) => updateValue(option, e)"
                />
              </template>
              <template v-if="inputTypeKeyMap[option] === 'selectUnit'">
                <SingleSelectInput
                  :index="index + 9"
                  :label="t(option) + getRequiredMark(option)"
                  :model-value="fieldState[option].value.value"
                  :placeholder="t(option)"
                  :filter-type="keyMappingForSelectInput[option]"
                  :is-error="fieldState[option]?.isError.value"
                  :error-message="fieldState[option]?.errorMessage.value"
                  :prop-options="unitOptions.map((o) => t(o))"
                  @click:select-input="() => (currentOpenSelectInputName = option)"
                  @update:model-value="(e) => updateValue(option, e)"
                />
              </template>
              <template v-else>
                <TextInput
                  :model-value="fieldState[option].value.value"
                  :label="t(option) + getRequiredMark(option)"
                  :placeholder="t(option)"
                  :is-error="fieldState[option].isError.value"
                  :error-message="fieldState[option].errorMessage.value"
                  @update:model-value="(e) => updateValue(option, e)"
                />
              </template>
            </div>
          </template>
        </div>
      </template>
      <template v-else>
        <div class="edit-individual-modal__container edit-individual-modal__container--company">
          <template v-for="(option, index) in formOptionsBusiness" :key="option">
            <div class="edit-individual-modal__item">
              <SingleSelectInput
                :index="index"
                :label="t(option) + getRequiredMark(option)"
                :model-value="fieldState[option].value.value"
                :placeholder="t(option)"
                :filter-type="keyMappingForSelectInput[option]"
                :is-error="fieldState[option]?.isError.value"
                :error-message="fieldState[option]?.errorMessage.value"
                @click:select-input="() => (currentOpenSelectInputName = option)"
                @update:model-value="(e) => updateValue(option, e)"
              />
            </div>
          </template>
        </div>
      </template>
      <!-- 新規選択肢追加 -->
      <AddOptionSection :options="store.getAddableOptions(isEditAggregateBusiness)" @set-option="addNewOption" />
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.edit-individual-modal {
  &__container {
    display: grid;
    width: 100%;

    padding: 16px;
    border-radius: 8px;
    column-gap: 8px;
    row-gap: 16px;

    background-color: $backgroundDefault;

    &--base-data {
      grid-template-columns: repeat(20, 1fr);
    }

    &--amount {
      grid-template-columns: repeat(3, 1fr);
    }

    &--company {
      grid-template-columns: repeat(5, 1fr);
    }
  }

  &__item {
    display: grid;
    grid-template-columns: 100%;

    box-sizing: border-box;
    border-bottom: solid 1px $borderDefault;

    &--base-data {
      &--upper {
        grid-column: span 5;
      }

      &--lower {
        grid-column: span 4;
      }
    }
  }
}
</style>
